import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../../interfaces/Global';
import { useGerenciarMunicipios } from '../../hooks/useGerenciarMunicipios';
import { convertDataUSParaBrasilEHora } from '../../../../services/Conversores';
import { Modal, Tabs, Tag, Typography, Button, Spin } from 'antd';
import { IGerenciarMunicipiosItem, IPeriodoReprocessar } from '../../interface';
import { LockOutlined, ReloadOutlined, CloseOutlined, EyeOutlined, EyeInvisibleOutlined, CheckSquareOutlined, BorderOutlined } from '@ant-design/icons';

const { Text } = Typography;

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const formatDate = (data?: string | null | Date) => {
    if (!data) return '-';
    const formatted = convertDataUSParaBrasilEHora(data);
    return formatted || String(data);
};

interface ModalRepublicarMunicipioProps {
    visible: boolean;
    municipio: IGerenciarMunicipiosItem | null;
    onClose: () => void;
    gerenciarHook: ReturnType<typeof useGerenciarMunicipios>;
}

export const ModalRepublicarMunicipio: React.FC<ModalRepublicarMunicipioProps> = ({
    visible,
    municipio,
    onClose,
    gerenciarHook,
}) => {

    const propsState = useSelector((state: stateGlobal) => state.global);
    const {
        loadingDetalhes,
        detalhesMunicipio,
        loadingReprocessar,
        reprocessarMunicipio,
        getDetalhesMunicipio,
    } = gerenciarHook;

    const [activeTab, setActiveTab] = useState('atributos');
    const [selectedPeriodos, setSelectedPeriodos] = useState<IPeriodoReprocessar[]>([]);
    const [revealedAtributos, setRevealedAtributos] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (visible && municipio) {
            setSelectedPeriodos([]);
            setRevealedAtributos({});
            setActiveTab('atributos');
            getDetalhesMunicipio(municipio.id_ibge, municipio.sistema_slug);
        }
    }, [visible, municipio, getDetalhesMunicipio]);

    if (!visible || !municipio) return null;

    const periodosList = detalhesMunicipio?.periodos || [];
    const atributosList = detalhesMunicipio?.atributos || [];

    const totalAnos = periodosList.length || (municipio.anos ? municipio.anos.length : 0);
    const totalMeses = periodosList.reduce((acc, p) => acc + (p.meses ? p.meses.length : 0), 0) || municipio.total_periodos || 0;

    const anos_string = Array.isArray(municipio.anos) && municipio.anos.length > 0
        ? municipio.anos.join(', ')
        : periodosList.map(p => p.ano).join(', ');

    const toggleRevealAtributo = (chave: string) => {
        setRevealedAtributos(prev => ({
            ...prev,
            [chave]: !prev[chave]
        }));
    };

    const handleToggleMonth = (ano: number, mes: number, isProcessed: boolean) => {
        if (!isProcessed) return;
        setSelectedPeriodos(prev => {
            const exists = prev.some(p => p.ano === ano && p.mes === mes);
            if (exists) {
                return prev.filter(p => !(p.ano === ano && p.mes === mes));
            } else {
                return [...prev, { ano, mes }];
            }
        });
    };

    const handleToggleYear = (ano: number, meses: number[]) => {
        if (!meses || meses.length === 0) return;
        setSelectedPeriodos(prev => {
            const yearProcessedPeriodos = meses.map(m => ({ ano, mes: m }));
            const isYearAllSelected = yearProcessedPeriodos.every(p => prev.some(sp => sp.ano === p.ano && sp.mes === p.mes));

            if (isYearAllSelected) {
                return prev.filter(sp => sp.ano !== ano);
            } else {
                const otherYearsPeriodos = prev.filter(sp => sp.ano !== ano);
                return [...otherYearsPeriodos, ...yearProcessedPeriodos];
            }
        });
    };

    const allProcessedPeriodos: IPeriodoReprocessar[] = [];
    periodosList.forEach(p => {
        if (Array.isArray(p.meses)) {
            p.meses.forEach(m => {
                allProcessedPeriodos.push({ ano: p.ano, mes: m });
            });
        }
    });

    const isAllSelected = allProcessedPeriodos.length > 0 && allProcessedPeriodos.every(p => selectedPeriodos.some(sp => sp.ano === p.ano && sp.mes === p.mes));

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedPeriodos([]);
        } else {
            setSelectedPeriodos(allProcessedPeriodos);
        }
    };

    const formatSelectedSummary = (periodos: IPeriodoReprocessar[]) => {
        const grouped: Record<number, number[]> = {};
        periodos.forEach(({ ano, mes }) => {
            if (!grouped[ano]) grouped[ano] = [];
            grouped[ano].push(mes);
        });

        const sortedYears = Object.keys(grouped).map(Number).sort((a, b) => b - a);

        const parts = sortedYears.map(ano => {
            const mesesSorted = grouped[ano].sort((a, b) => a - b);
            const mesesNames = mesesSorted.map(m => MESES_ABREV[m - 1]).join(', ');
            return `${ano} (${mesesNames})`;
        });

        return parts.join(' · ');
    };

    const handleReprocessar = async () => {
        if (selectedPeriodos.length === 0) return;
        const success = await reprocessarMunicipio(municipio.id_ibge, municipio.sistema_slug, selectedPeriodos);
        if (success) {
            onClose();
        }
    };

    const isDark = propsState.theme === 'dark';

    const tabItems = [
        {
            key: 'atributos',
            label: 'Atributos',
            children: (
                <div>
                    <div
                        style={{
                            backgroundColor: isDark ? '#262626' : '#F4F6F9',
                            border: `1px solid ${isDark ? '#434343' : '#E4E7EC'}`,
                            borderRadius: 6,
                            padding: '10px 14px',
                            marginBottom: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: isDark ? '#BFBFBF' : '#595959',
                            fontSize: 13,
                        }}
                    >
                        <LockOutlined style={{ color: '#8C8C8C' }} />
                        <span>Somente leitura. Estes atributos são gravados pela API no upsert e não podem ser alterados nesta tela.</span>
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 12,
                            maxHeight: 'calc(70vh - 220px)',
                            overflowY: 'auto',
                            paddingRight: 6,
                        }}
                    >
                        <div style={cardStyle(isDark)}>
                            <div style={cardLabelRowStyle}>
                                <Text type="secondary" style={{ fontSize: 12 }}>id_ibge</Text>
                                <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}>chave do upsert</Tag>
                            </div>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>{detalhesMunicipio?.id_ibge || municipio.id_ibge}</Text>
                        </div>
                        <div style={cardStyle(isDark)}>
                            <div style={cardLabelRowStyle}>
                                <Text type="secondary" style={{ fontSize: 12 }}>sistema</Text>
                                <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}>chave do upsert</Tag>
                            </div>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>{detalhesMunicipio?.sistema || municipio.sistema}</Text>
                        </div>
                        <div style={cardStyle(isDark)}>
                            <div style={cardLabelRowStyle}>
                                <Text type="secondary" style={{ fontSize: 12 }}>ano</Text>
                                <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}>chave do upsert</Tag>
                            </div>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>{anos_string || '-'}</Text>
                        </div>
                        <div style={cardStyle(isDark)}>
                            <div style={cardLabelRowStyle}>
                                <Text type="secondary" style={{ fontSize: 12 }}>mes</Text>
                                <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}>chave do upsert</Tag>
                            </div>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>presente na collection</Text>
                        </div>
                        {atributosList.map((attr, idx) => {
                            const isSensivel = attr.sensivel === true;
                            const isRevealed = !!revealedAtributos[attr.chave];
                            const rawValor = attr.valor !== null && attr.valor !== undefined ? String(attr.valor) : '-';
                            const displayValue = isSensivel && !isRevealed ? '••••••••' : rawValor;
                            return (
                                <div key={attr.chave + idx} style={cardStyle(isDark)}>
                                    <div style={cardLabelRowStyle}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{attr.chave}</Text>
                                        {isSensivel && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={isRevealed ? <EyeInvisibleOutlined style={{ fontSize: 14, color: '#1890FF' }} /> : <EyeOutlined style={{ fontSize: 14, color: '#8C8C8C' }} />}
                                                onClick={() => toggleRevealAtributo(attr.chave)}
                                                style={{ padding: 0, height: 20, width: 20, minWidth: 20 }}
                                            />
                                        )}
                                    </div>
                                    <Text style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>
                                        {displayValue}
                                    </Text>
                                </div>
                            );
                        })}
                        <div style={cardStyle(isDark)}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>atualizado_em</Text>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>
                                {formatDate(detalhesMunicipio?.atualizado_em || municipio.atualizado_em)}
                            </Text>
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: 'periodos',
            label: 'Períodos',
            children: (
                <div>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Clique nos meses já processados para selecioná-los e reprocessar. Meses nunca processados não podem ser selecionados.
                    </Text>
                    <div
                        style={{
                            justifyContent: 'flex-end',
                            display: 'flex'
                        }}
                    >
                        <Button
                            type="link"
                            icon={isAllSelected ? <CheckSquareOutlined /> : <BorderOutlined />}
                            onClick={handleToggleSelectAll}
                            style={{ padding: 0, fontSize: 13 }}
                        >
                            {isAllSelected ? 'desmarcar todos os períodos' : 'selecionar todos os períodos processados'}
                        </Button>
                    </div>
                    <div
                        style={{
                            maxHeight: 'calc(70vh - 220px)',
                            overflowY: 'auto',
                            paddingRight: 6,
                            margin: '10px 0',
                        }}
                    >
                        {periodosList.length === 0 ? (
                            <div style={{ padding: 24, textAlign: 'center' }}>
                                <Text type="secondary">Nenhum período registrado para este município.</Text>
                            </div>
                        ) : (
                            periodosList.map((periodo) => {
                                const processedMonths = periodo.meses || [];
                                const yearProcessedPeriodos = processedMonths.map(m => ({ ano: periodo.ano, mes: m }));
                                const isYearAllSelected = processedMonths.length > 0 && yearProcessedPeriodos.every(p => selectedPeriodos.some(sp => sp.ano === p.ano && sp.mes === p.mes));

                                return (
                                    <div
                                        key={periodo.ano}
                                        style={{
                                            backgroundColor: isDark ? '#1F1F1F' : '#FAFAFA',
                                            border: `1px solid ${isDark ? '#303030' : '#E8E8E8'}`,
                                            borderRadius: 6,
                                            padding: '12px 16px',
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                            <Tag
                                                color={isYearAllSelected ? 'blue' : undefined}
                                                onClick={() => handleToggleYear(periodo.ano, processedMonths)}
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    padding: '2px 10px',
                                                    background: isYearAllSelected ? undefined : (isDark ? '#262626' : '#FFF'),
                                                    margin: 0,
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    border: isYearAllSelected ? undefined : `1px solid ${isDark ? '#434343' : '#D9D9D9'}`,
                                                }}
                                                title="Clique para selecionar/desmarcar todos os meses deste ano"
                                            >
                                                {periodo.ano}
                                            </Tag>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {processedMonths.length} de 12 meses processados · atualizado em {formatDate(periodo.atualizado_em)}
                                            </Text>
                                        </div>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(12, 1fr)',
                                                gap: 6,
                                            }}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map((mesIndex) => {
                                                const isProcessed = processedMonths.includes(mesIndex);
                                                const isSelected = selectedPeriodos.some(p => p.ano === periodo.ano && p.mes === mesIndex);

                                                return (
                                                    <Button
                                                        key={mesIndex}
                                                        size="small"
                                                        disabled={!isProcessed}
                                                        onClick={() => handleToggleMonth(periodo.ano, mesIndex, isProcessed)}
                                                        style={monthButtonStyle(isProcessed, isSelected, isDark)}
                                                    >
                                                        {MESES_ABREV[mesIndex - 1]}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    {selectedPeriodos.length > 0 && (
                        <div
                            style={{
                                backgroundColor: isDark ? '#111D2C' : '#E6F7FF',
                                border: `1px solid ${isDark ? '#15395B' : '#BAE7FF'}`,
                                borderRadius: 6,
                                padding: '10px 14px',
                                marginTop: 12,
                            }}
                        >
                            <Text style={{ color: isDark ? '#177DDC' : '#0050B3', fontSize: 13, fontWeight: 500 }}>
                                {selectedPeriodos.length} período(s) selecionado(s) para reprocessar: {formatSelectedSummary(selectedPeriodos)}
                            </Text>
                        </div>
                    )}
                </div>
            )
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={850}
            bodyStyle={{ padding: '20px 24px' }}
            style={{ borderRadius: 8, overflow: 'hidden' }}
            centered
        >
            <Spin
                spinning={loadingDetalhes}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 16
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                marginBottom: 4
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 20,
                                    fontWeight: 700
                                }}
                            >
                                {detalhesMunicipio?.nome || municipio.nome}
                            </span>
                            <Text type="secondary"
                                style={{
                                    fontSize: 14
                                }}
                            >
                                {detalhesMunicipio?.uf || municipio.uf} · IBGE {detalhesMunicipio?.id_ibge || municipio.id_ibge}
                            </Text>
                            <Tag
                                color="blue"
                            >
                                {detalhesMunicipio?.sistema || municipio.sistema}
                            </Tag>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Último processamento: {formatDate(detalhesMunicipio?.atualizado_em || municipio.atualizado_em)} · {totalAnos} anos · {totalMeses} meses processados
                        </Text>
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined style={{ fontSize: 16, color: '#8C8C8C' }} />}
                        onClick={onClose}
                    />
                </div>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
                <div
                    style={{
                        paddingTop: 16,
                        marginTop: 16,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        borderTop: `1px solid ${isDark ? '#303030' : '#F0F0F0'}`,
                    }}
                >
                    <Button
                        onClick={onClose}
                        style={{
                            backgroundColor: '#6C757D',
                            color: '#FFF',
                            borderColor: '#6C757D',
                            borderRadius: 4,
                            height: 36,
                            padding: '0 20px',
                            fontWeight: 500,
                        }}
                    >
                        Fechar
                    </Button>
                    <Button
                        disabled={selectedPeriodos.length === 0}
                        loading={loadingReprocessar}
                        onClick={handleReprocessar}
                        icon={<ReloadOutlined />}
                        style={{
                            backgroundColor: selectedPeriodos.length > 0 ? '#28A745' : '#88C999',
                            borderColor: 'transparent',
                            color: '#FFF',
                            borderRadius: 4,
                            height: 36,
                            padding: '0 20px',
                            fontWeight: 500,
                            opacity: selectedPeriodos.length > 0 ? 1 : 0.6,
                            cursor: selectedPeriodos.length > 0 ? 'pointer' : 'not-allowed',
                        }}
                    >
                        Reprocessar {selectedPeriodos.length > 0 ? `${selectedPeriodos.length} período(s)` : ''}
                    </Button>
                </div>
            </Spin>
        </Modal>
    );
};

const cardStyle = (isDark: boolean): React.CSSProperties => ({
    backgroundColor: isDark ? '#1F1F1F' : '#FFF',
    border: `1px solid ${isDark ? '#303030' : '#E8E8E8'}`,
    borderRadius: 6,
    padding: '10px 12px',
});

const cardLabelRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
};

const monthButtonStyle = (isProcessed: boolean, isSelected: boolean, isDark: boolean): React.CSSProperties => {
    if (!isProcessed) {
        return {
            backgroundColor: isDark ? '#262626' : '#F5F5F5',
            borderColor: isDark ? '#303030' : '#E8E8E8',
            color: isDark ? '#595959' : '#BFBFBF',
            cursor: 'not-allowed',
            height: 32,
            padding: 0,
            fontSize: 12,
        };
    }

    if (isSelected) {
        return {
            backgroundColor: '#1890FF',
            borderColor: '#1890FF',
            color: '#FFF',
            fontWeight: 600,
            cursor: 'pointer',
            height: 32,
            padding: 0,
            fontSize: 12,
        };
    }

    return {
        backgroundColor: isDark ? '#141414' : '#FFF',
        borderColor: isDark ? '#434343' : '#D9D9D9',
        color: isDark ? '#DBDBDB' : '#262626',
        fontWeight: 500,
        cursor: 'pointer',
        height: 32,
        padding: 0,
        fontSize: 12,
    };
};
